import { useMutation, useQuery, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  IntegrationsPlaygroundService,
  type PlaygroundInvokeRequest,
  type PlaygroundItemsDateRangeRequest,
  type PlaygroundItemsIdsRequest,
} from '../api/integrations-playground-service';
import type {
  PlaygroundInvokeResult,
  PlaygroundItemsResult,
  PlaygroundObjectsResult,
} from '../api/types';

export const playgroundQueryKeys = {
  all: ['integrations-playground'] as const,
  objects: (accountId: string, integrationKey: string) =>
    ['integrations-playground', 'objects', accountId, integrationKey] as const,
};

export function usePlaygroundObjects(
  accountId: string,
  integrationKey: string,
): UseQueryResult<PlaygroundObjectsResult> {
  return useQuery({
    queryKey: playgroundQueryKeys.objects(accountId, integrationKey),
    queryFn: () => IntegrationsPlaygroundService.getObjects({ accountId, integrationKey }),
    enabled: Boolean(accountId) && Boolean(integrationKey),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export type PlaygroundItemsRequest =
  | { mode: 'ids'; request: PlaygroundItemsIdsRequest }
  | { mode: 'dateRange'; request: PlaygroundItemsDateRangeRequest };

export function usePlaygroundItems(): UseMutationResult<
  PlaygroundItemsResult,
  unknown,
  PlaygroundItemsRequest
> {
  return useMutation({
    mutationFn: (input: PlaygroundItemsRequest) =>
      input.mode === 'ids'
        ? IntegrationsPlaygroundService.getItemsByIds(input.request)
        : IntegrationsPlaygroundService.getItemsByDateRange(input.request),
  });
}

export function usePlaygroundInvoke(): UseMutationResult<
  PlaygroundInvokeResult,
  unknown,
  PlaygroundInvokeRequest
> {
  return useMutation({
    mutationFn: (request: PlaygroundInvokeRequest) => IntegrationsPlaygroundService.invoke(request),
  });
}
